// src/context/SoftphoneContext.js
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import axios from "axios";
import { Device } from "@twilio/voice-sdk";

const SoftphoneContext = createContext(null);
export const useSoftphone = () => useContext(SoftphoneContext);

export function SoftphoneProvider({ user, children }) {
  const [softphoneStatus, setSoftphoneStatus] = useState("Uninitialized");
  const [activeCall, setActiveCall] = useState(null);
  const [incomingFrom, setIncomingFrom] = useState(null);
  const [isEarpiece, setIsEarpiece] = useState(true);

  // Keep Device alive across renders
  const deviceRef = useRef(null);
  const initPromiseRef = useRef(null);

  // Notify Flutter for audio routing
  const notifyFlutter = useCallback((event) => {
    if (window.flutter_inappwebview?.callHandler) {
      console.log(`Audio → Flutter: ${event}`);
      window.flutter_inappwebview.callHandler("callEvent", event);
    }
  }, []);

  // Toggle earpiece/speaker
  const toggleAudio = useCallback(() => {
    const newMode = !isEarpiece;
    setIsEarpiece(newMode);
    notifyFlutter(newMode ? "earpiece" : "speaker");
  }, [isEarpiece, notifyFlutter]);

  // Hang up call
  const hangUp = useCallback(() => {
    if (activeCall) {
      activeCall.disconnect();
    }
    setActiveCall(null);
    setIncomingFrom(null);
  }, [activeCall]);

  // Initialize Twilio Device
  const setupDevice = useCallback(async () => {
    if (!user?.id) return;
    if (deviceRef.current) return;
    if (initPromiseRef.current) return initPromiseRef.current;

    const promise = (async () => {
      setSoftphoneStatus("Fetching token...");
      try {
        const { data } = await axios.post("/api/auth/token", {
          identity: user.id,
        });

        const device = new Device(data.token, { debug: true });
        deviceRef.current = device;

        device.on("ready", () => {
          console.log("Twilio Device ready!");
          setSoftphoneStatus("Device ready");
        });
        device.on("error", (err) => {
          console.log("Twilio Device erorr!", err);
          setSoftphoneStatus(`Error: ${err.message}`);
        });

        device.on("connect", (call) => {
          console.log("Twilio Device call!", call);
          setActiveCall(call);
          setSoftphoneStatus("Call connected");
          setIsEarpiece(true);
          notifyFlutter("earpiece");
        });

        device.on("disconnect", () => {
          console.log("Twilio Device disconnect!");
          setActiveCall(null);
          setIncomingFrom(null);
          setSoftphoneStatus("Call ended");
          setIsEarpiece(true);
          notifyFlutter("earpiece");
        });

        device.on("incoming", (call) => {
          console.log("Twilio Device incoming!");
          setIncomingFrom(call.parameters.From);
          setActiveCall(call);
          setSoftphoneStatus(`Incoming from ${call.parameters.From}`);
          call.on("disconnect", hangUp);
        });

        device.on("callStatus", (call) => {
          if (call.status() === "closed") {
            setActiveCall(null);
            setSoftphoneStatus("Call ended");
          }
        });
        console.log("Twilio Device initialized");
      } catch (err) {
        setSoftphoneStatus(`Init failed: ${err.message}`);
        console.error("Device init error:", err);
      }
    })();

    initPromiseRef.current = promise;
    await promise;
    initPromiseRef.current = null;
  }, [user?.id, notifyFlutter, hangUp]);

  // Auto-setup when user is loaded
  useEffect(() => {
    console.log("User loaded:", user);
    console.log("deviceRef:", deviceRef.current);
    console.log(
      "user?.id && !deviceRef.current:",
      user?.id && !deviceRef.current
    );
    if (user?.id && !deviceRef.current) {
      console.log("Setting up device...");
      setupDevice();
    }
  }, [user, setupDevice]);

  // Cleanup on unmount/logout
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, []);

  // Auto-notify Flutter 2 seconds after a call becomes active
  useEffect(() => {
    if (activeCall) {
      const timer = setTimeout(() => {
        console.log(
          "🔊 Sending delayed 'earpiece' event to Flutter (2s after call start)"
        );
        notifyFlutter("earpiece");
        setIsEarpiece(true);
      }, 300);

      // Cleanup in case call ends early
      return () => clearTimeout(timer);
    }
  }, [activeCall, notifyFlutter]);

  // Make outgoing call
  const makeTwilioCall = useCallback(
    async (to) => {
      if (!deviceRef.current) throw new Error("Device not ready");
      setSoftphoneStatus("Connecting...");

      try {
        const call = await deviceRef.current.connect({ params: { To: to } });
        setActiveCall(call);
        setIsEarpiece(true);
        notifyFlutter("earpiece");
        //  LOG 2: Successful connection initiation
        console.log(`[Twilio Call] Device connection initiated.`);

        call.on("ringing", () => {
          //  LOG 3: Ringing
          console.log("[Twilio Call] Status: Ringing.");
          setSoftphoneStatus("Ringing...");
          notifyFlutter("earpiece");
        });

        call.on("accept", () => {
          //  LOG 4: Accepted
          console.log("[Twilio Call] Status: Accepted/Connected.");
          setSoftphoneStatus("Call connected");
          notifyFlutter("earpiece");
        });

        call.on("disconnect", hangUp);

        //  LOG 5: Log to backend
        console.log(
          "[Twilio Call] Sending log to local backend /api/calls/outgoing"
        );
        // Log to backend
        // axios.post("/api/calls/outgoing", { to }).catch(() => {});

        return call;
      } catch (err) {
        setSoftphoneStatus(`Call failed: ${err.message}`);
        throw err;
      }
    },
    [hangUp, notifyFlutter]
  );

  const value = {
    softphoneStatus,
    activeCall,
    incomingFrom,
    makeTwilioCall,
    hangUp,
    answerCall: () => activeCall?.accept(),
    toggleAudio,
    isEarpiece,
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
}
